import { CheckCircle, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useOffert } from "@/components/OffertModal";
import { FadeUp, FadeLeft, FadeRight } from "@/components/ScrollAnimations";

interface ComparisonItem {
  label: string;
  nordiska: boolean;
  diy: boolean;
}

const items: ComparisonItem[] = [
  { label: "Skräddarsydd konstruktion efter ditt hems mått", nordiska: true, diy: false },
  { label: "Egen fabrik med full kvalitetskontroll", nordiska: true, diy: false },
  { label: "Markarbete och grundläggning", nordiska: true, diy: false },
  { label: "El- och VVS-installation", nordiska: true, diy: false },
  { label: "Professionell montering av egna montörer", nordiska: true, diy: false },
  { label: "Bygglovsansökan och ritningar", nordiska: true, diy: false },
  { label: "Fast pris utan dolda kostnader", nordiska: true, diy: false },
  { label: "En kontaktperson genom hela projektet", nordiska: true, diy: false },
  { label: "Slutbesiktning och garanti", nordiska: true, diy: false },
  { label: "Lägre ingångspris", nordiska: false, diy: true },
];

export default function ComparisonSection() {
  const { openOffert } = useOffert();

  return (
    <section className="py-10 sm:py-16 lg:py-20 bg-sand">
      <div className="container max-w-4xl">
        <FadeUp>
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-copper font-heading font-bold text-xs sm:text-sm tracking-widest uppercase mb-3">
              Varför välja helhetslösning?
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-navy mb-4">
              Helhetslösning vs. att göra det själv
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              Att köpa glaspartier och koordinera underleverantörer själv kan verka billigare.
              Men den verkliga kostnaden visar sig ofta i tid, stress och kompromisser med kvaliteten.
            </p>
          </div>
        </FadeUp>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <FadeLeft>
            <div className="bg-navy rounded-xl p-6 sm:p-8 text-white h-full">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-rose/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-rose" />
                </div>
                <h3 className="font-heading font-bold text-xl">Nordiska Inglasningar</h3>
              </div>
              <p className="text-white/70 text-sm leading-relaxed mb-6">
                Vi sköter helheten. Du behöver inte bolla med någon annan än med oss.
                Markarbetare, platssättare, elektriker, glasmästare och montörer ingår.
              </p>
              <ul className="space-y-3">
                {items.filter(i => i.nordiska).map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2.5">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                    <span className="text-sm text-white/85">{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </FadeLeft>

          <FadeRight>
            <div className="bg-white rounded-xl p-6 sm:p-8 border border-border/50 h-full">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <X className="w-5 h-5 text-gray-400" />
                </div>
                <h3 className="font-heading font-bold text-xl text-navy">Köpa och koordinera själv</h3>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                Du köper standardpartier och ansvarar själv för att hitta och koordinera
                alla hantverkare. Resultatet beror på din egen projektledning.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2.5">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                  <span className="text-sm text-muted-foreground">Lägre ingångspris på material</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <span className="text-sm text-muted-foreground">Standardpartier som kanske inte passar ditt hus</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <span className="text-sm text-muted-foreground">Du måste hitta och boka markarbetare, elektriker, VVS</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <span className="text-sm text-muted-foreground">Du är projektledare och ansvarar för tidsplanen</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <span className="text-sm text-muted-foreground">Ingen samlad garanti, varje leverantör har sin</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <span className="text-sm text-muted-foreground">Risk för fördyringar och förseningar</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <span className="text-sm text-muted-foreground">Bygglovsansökan och ritningar ordnar du själv</span>
                </li>
              </ul>
            </div>
          </FadeRight>
        </div>

        <FadeUp>
          <div className="bg-white rounded-xl p-5 sm:p-6 border border-border/50 mb-8">
            <p className="text-navy font-heading font-bold text-base sm:text-lg mb-2">
              Vad kunder som provat båda säger
            </p>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed italic">
              "Vi försökte först köpa glaspartier och ordna resten själva. Efter tre månader av
              jakt på hantverkare och standardpartier som inte passade vårt hus gav vi upp och
              ringde Nordiska. De löste allt på sex veckor. I efterhand önskar vi att vi börjat där."
            </p>
            <p className="text-muted-foreground text-xs mt-2">Kund i Göteborg, Vinter Plus 18 kvm</p>
          </div>

          <div className="text-center">
            <Button
              size="lg"
              onClick={() => openOffert("comparison-cta")}
              className="bg-rose hover:bg-rose-dark text-white font-medium px-8 py-6 text-base"
            >
              Låt oss sköta helheten
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-muted-foreground text-xs mt-3">
              Kostnadsfritt hembesök, helt kravlöst.
            </p>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
